import React, { useState } from 'react'
import UserAvatar from '../shared/UserAvatar'
import { trackEvent } from 'utils/analytics'

export default function TeamReviewsSection({ reviews, colors, translations = {} }) {
  const [showAll, setShowAll] = useState(false)

  if (!reviews || reviews.length === 0) {
    return null
  }

  const StarRating = ({ rating, size = 'w-4 h-4' }) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`${size} ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    )
  }

  const averageRating = (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)

  // Aggregate tag statistics
  const tagCounts = {}
  reviews.forEach(r => {
    (r.tags || []).forEach(tag => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1
    })
  })
  const tagStats = Object.entries(tagCounts)
    .map(([tag, count]) => ({ tag, count, percentage: Math.round((count / reviews.length) * 100) }))
    .sort((a, b) => b.count - a.count)

  const displayedReviews = showAll ? reviews : reviews.slice(0, 6)

  return (
    <section className="py-8 border-t border-gray-200">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-[22px] font-semibold" style={{ color: '#222222' }}>{translations.title || 'Student Reviews'}</h2>
            <p className="text-sm mt-1" style={{ color: '#717171' }}>{translations.subtitle || 'Genuine feedback from students'}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-bold" style={{ color: colors.primary }}>
              {averageRating}
            </span>
            <div>
              <StarRating rating={Math.round(averageRating)} />
              <span className="text-xs text-gray-500">
                {(reviews.length === 1
                  ? (translations.review_count_one || '%{count} review')
                  : (translations.review_count_other || '%{count} reviews')
                ).replace('%{count}', reviews.length)}
              </span>
            </div>
          </div>
        </div>

        {/* Tag summary */}
        {tagStats.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {tagStats.map(({ tag, percentage }) => (
              <span
                key={tag}
                className="px-3 py-1.5 text-xs font-medium rounded-full"
                style={{ backgroundColor: colors.background, color: colors.primaryDark }}
              >
                {tag} {percentage}%
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        {displayedReviews.map((review) => (
          <div
            key={review.id}
            className="border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow"
          >
            <div className="flex items-start gap-4">
              <UserAvatar
                src={review.studentAvatarUrl}
                name={review.studentName}
                size="md"
                fallbackColor={colors.primary}
                className="flex-shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h4 className="font-semibold text-gray-900">{review.studentName}</h4>
                    {review.teacherName && (
                      <span className="text-xs text-gray-500">{translations.teacher_label || 'Teacher:'} {review.teacherName}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <StarRating rating={review.rating} />
                    <span className="text-xs text-gray-400">{review.createdAt}</span>
                  </div>
                </div>

                {/* Tags */}
                {review.tags && review.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {review.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs rounded-full bg-blue-50 text-blue-700"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {review.content && (
                  <p className="text-sm text-gray-600 leading-relaxed">{review.content}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {reviews.length > 6 && (
        <div className="mt-6 text-center">
          <button
            onClick={() => {
              if (!showAll) trackEvent('reviews_expand', { total_reviews: reviews.length })
              setShowAll(!showAll)
            }}
            className="px-6 py-2 text-sm font-medium rounded-lg border transition-colors cursor-pointer"
            style={{
              borderColor: colors.primary,
              color: colors.primary,
            }}
          >
            {showAll
              ? (translations.show_less || 'Show less')
              : (translations.view_all || 'View all %{count} reviews').replace('%{count}', reviews.length)
            }
          </button>
        </div>
      )}
    </section>
  )
}
